import {
  DashboardState,
  ESdChangeReason,
  EUserAccessPart,
  LeftPanelType,
  RightPanelType,
  ViewPart,
  Visibility
} from "@kuika/kuika-cl-model";
import { createRef } from "react";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../shared/pure-sd";
import chatbotEvents from "../../../shared/utility/chatbot-events";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { initMcpToolBridge, destroyMcpToolBridge } from "../../../shared/utility/mcp-tool-bridge";
import BoardNavBadge from "../../management/board/board-nav-badge";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaSelectionIndicator from "../../ui-kit/kuika-selection-indicator/kuika-selection-indicator";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import { ShortCutInfo } from "../footer/short-cut-info";
import { AcademyButton } from "./academy-button";
import { ActionTreeButton } from "./action-tree-button";
import { AuditButton } from "./audit-button";
import BoardOverlay from "./board-overlay/board-overlay";
// import { ChatbotButton } from "./chatbot-button";
import { ChatbotWindow } from "./chatbot-panel/chatbot-window";
import CustomChatbot from "./chatbot-panel/custom-chatbot";
import { CommentsButton } from "./comments-button";
import { ComponentTreeButton } from "./component-tree-button";
import { ComponentsButton } from "./components-button";
import "./design-left-menu.scss";
import { DatasourcesButton } from "./datasource-button";
import { HelpdeskButton } from "./helpdesk-button";
import { LanguageButton } from "./language-button";
import { handleLeftPanelRailClick } from "./left-panel-rail";
import McpChatPanel from "./mcp-chat/panel/mcp-chat-panel";
import { getMcpChatPanelInstance, MCP_CHAT_UNDOCK_EVENT } from "./mcp-chat/panel/mcp-chat-panel-ref";
import type { ChatAnchor } from "./mcp-chat/shared/mcp-chat-types";
import { ReportsButton } from "./reports-button";
import { ScreensButton } from "./screens-button";

/**
 * The active left panel's button. The toggles keep their own active fill instead of the sliding
 * one: the board overlay, the helpdesk modal and the academy / shortcut menus open over the
 * panel rather than replacing it.
 */
const ACTIVE_PANEL_BUTTON_SELECTOR: string =
  ".kuika-button__active:not(.kuika-left-menu__board-button, .kuika-helpdesk-button, .kuika-left-menu__academy-button)";

export interface IDesignLeftMenuState {
  isChatbotPanelVisible: boolean;
  isBoardOverlayVisible: boolean;
}

export interface IDesignLeftMenuProps {}

class DesignLeftMenu extends PureSdComponent<
  IDesignLeftMenuProps & IWithSDProps & IWithContextProp,
  IDesignLeftMenuState
> {
  private unsubscribeChatbotEvents: (() => void) | null = null;
  mcpChatPanelRef = createRef<McpChatPanel>();

  constructor(props: IDesignLeftMenuProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      isChatbotPanelVisible: false,
      isBoardOverlayVisible: false
    };
  }

  componentDidMount(): void {
    this.unsubscribeChatbotEvents = chatbotEvents.subscribe((isVisible) => {
      if (this.state.isChatbotPanelVisible !== isVisible) {
        this.setState({ isChatbotPanelVisible: isVisible });
      }
    });
    initMcpToolBridge();
    window.addEventListener(MCP_CHAT_UNDOCK_EVENT, this.handleUndockEvent as EventListener);
  }

  componentWillUnmount(): void {
    if (this.unsubscribeChatbotEvents) {
      this.unsubscribeChatbotEvents();
    }
    // Cancel active AI requests before destroying the bridge
    getMcpChatPanelInstance()?.cancelAllActiveRequests();
    destroyMcpToolBridge();
    window.removeEventListener(MCP_CHAT_UNDOCK_EVENT, this.handleUndockEvent as EventListener);
  }

  componentDidUpdate(prevProps: any): void {
    const hasUserAccessCreateComponent = KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
      EUserAccessPart.CreateComponent,
      this.props.sd.project?.projectAccess
    );
    if (
      !hasUserAccessCreateComponent &&
      this.props.sd?.project?.projectAccess?.length !== prevProps.sd.project?.projectAccess?.length
    ) {
      this.props.sd.setActiveLeftPanelByClick(LeftPanelType.Screens);
    }

    // Close MCP chat panel when switching to report designer
    if (
      this.props.sd.dashboardState === DashboardState.reportDesigner &&
      prevProps.sd.dashboardState !== DashboardState.reportDesigner
    ) {
      getMcpChatPanelInstance()?.cancelAllActiveRequests();
      if (this.props.sd.isMcpChatVisible) {
        this.setIsMcpChatPanelVisible(false);
      }
    }

    // Auto-undock chat when leaving design state (right-panel docking is only available in design)
    if (
      this.props.sd.chatDockedToPanel === "right" &&
      prevProps.sd.dashboardState === DashboardState.design &&
      this.props.sd.dashboardState !== DashboardState.design
    ) {
      this.handleUndockChat("bottom-right");
    }
  }

  setIsChatbotPanelVisible = (isChatbotPanelVisible: boolean) => {
    this.setState({ isChatbotPanelVisible });
    chatbotEvents.setChatbotVisibility(isChatbotPanelVisible);
  };

  // Floating-chat visibility lives on the SD context (design-situation-provider) so the top-bar
  // trigger can toggle it and read it back for its active state; this stays as the panel's
  // prop-shaped entry point.
  setIsMcpChatPanelVisible = (isMcpChatPanelVisible: boolean) => {
    this.props.sd.setIsMcpChatVisible(isMcpChatPanelVisible);
  };

  toggleBoardOverlay = () => {
    this.setState((prev: IDesignLeftMenuState) => ({ isBoardOverlayVisible: !prev.isBoardOverlayVisible }));
  };

  handleMenuItemClick = (panelType: LeftPanelType) => {
    if (this.state.isChatbotPanelVisible) {
      this.setIsChatbotPanelVisible(false);
    }

    if (panelType === LeftPanelType.Zendesk) {
      return;
    }

    // One rail click, one toggle: closes the panel that is already open and showing, exactly
    // like the panel's own edge collapse button; otherwise opens it. See left-panel-rail.ts.
    handleLeftPanelRailClick(this.props.sd, panelType);
  };

  handleDockChat = (anchor: ChatAnchor) => {
    if (anchor === "right-panel") {
      // Only allow docking in design state where the properties panel is available
      if (this.props.sd.dashboardState !== DashboardState.design) return;
      // Expand right panel if collapsed
      if (this.props.sd.isRightPanelCollapsed?.[this.props.sd.dashboardState]) {
        this.props.sd.setRightPanelCollapsed(false);
      }
      this.props.sd.setChatDockedToPanel("right");
      this.props.sd.setActiveRightPanelByClick(RightPanelType.AIChat);
      this.setIsMcpChatPanelVisible(false);
    }
  };

  handleUndockChat = (anchor: ChatAnchor) => {
    const panel = this.mcpChatPanelRef.current;
    if (panel) {
      panel.setAnchor(anchor);
    }
    // Clear docked state and switch right panel back
    this.props.sd.setChatDockedToPanel(null);
    this.props.sd.setActiveRightPanelByClick(RightPanelType.Properties);
    this.setIsMcpChatPanelVisible(true);
  };

  handleUndockEvent = (e: CustomEvent) => {
    const anchor = (e.detail?.anchor || "bottom-right") as ChatAnchor;
    const dragStart = e.detail?.dragStart as { clientX: number; clientY: number } | undefined;

    if (dragStart) {
      // Undock and immediately enter drag mode
      this.props.sd.setChatDockedToPanel(null);
      this.props.sd.setActiveRightPanelByClick(RightPanelType.Properties);
      this.setIsMcpChatPanelVisible(true);
      // Wait a frame for the floating panel to mount/become visible
      requestAnimationFrame(() => {
        const panel = this.mcpChatPanelRef.current;
        if (panel) {
          panel.startDragFromDock(dragStart.clientX, dragStart.clientY);
        }
      });
    } else {
      this.handleUndockChat(anchor);
    }
  };

  render() {
    const hasUserAccessCreateComponent = KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
      EUserAccessPart.CreateComponent,
      this.props.sd.project?.projectAccess
    );
    const hasUserAccessManageDatasource = KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
      EUserAccessPart.ManageDatasource,
      this.props.sd.project?.projectAccess
    );
    // const hasUserAccessManageScreens = KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
    //   EUserAccessPart.ManageScreens,
    //   this.props.sd.project?.projectAccess
    // );
    const hasUserAccessManageReportDesigns = KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
      EUserAccessPart.ManageReportDesigns,
      this.props.sd.project?.projectAccess
    );
    const hasAccessBindDataToComponent = KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
      EUserAccessPart.BindDataToComponent,
      this.props.sd.project?.projectAccess
    );
    const isMainAI = KMainFunctions.isMainAI();
    return (
      <>
        {this.state.isBoardOverlayVisible && (
          <BoardOverlay
            projectId={this.props.sd.project?.id}
            onClose={() => this.setState({ isBoardOverlayVisible: false })}
          />
        )}
        <McpChatPanel
          ref={this.mcpChatPanelRef}
          isMcpChatPanelVisible={this.props.sd.isMcpChatVisible}
          setIsMcpChatPanelVisible={this.setIsMcpChatPanelVisible}
          onDockToPanel={this.handleDockChat}
          canDockToRightPanel={this.props.sd.dashboardState === DashboardState.design}
        />
        {this.props.sd.getVisibility(ViewPart.LeftMenu) === Visibility.Visible && (
          <div className="kuika-left-menu">
            <div
              data-testid="kuikaLeftMenu"
              data-onboarding-id="left_menu_container"
              className="kuika-left-menu__menu-items-wrapper"
            >
              <KuikaSelectionIndicator activeSelector={ACTIVE_PANEL_BUTTON_SELECTOR} />
              <div className="kuika-left-menu__menu-items">
                {hasUserAccessCreateComponent && <ComponentsButton onMenuItemClick={this.handleMenuItemClick} />}
                <ScreensButton onMenuItemClick={this.handleMenuItemClick} />
                {hasUserAccessManageReportDesigns && <ReportsButton onMenuItemClick={this.handleMenuItemClick} />}
                {/* The workflow list and fields buttons are retired (KOA-15840): the process
                    list opens with the workflow dashboard itself and the fields surface was
                    replaced by the process editor's Parameters tab. */}
                {/* The Scheduled Jobs rail button is retired: it only toggled the jobs
                    left panel, whose list now IS the tool's landing page. The tool is
                    reached from the header's tool tabs. */}
                {/* The API Builder rail button is retired: it only toggled the API's left
                    panel, whose list now IS the tool's landing page. The tool is reached
                    from the header's tool tabs. */}
                {/* The branching rail button is retired: it only toggled the branch-list left
                    panel, which now lives inside the branching workspace card. The tool itself
                    is reached via the header branch pill ("Manage all branches"). */}
                {hasUserAccessManageDatasource && <DatasourcesButton onMenuItemClick={this.handleMenuItemClick} />}
                {hasAccessBindDataToComponent && <ActionTreeButton onMenuItemClick={this.handleMenuItemClick} />}
                <ComponentTreeButton onMenuItemClick={this.handleMenuItemClick} />
                {hasUserAccessCreateComponent && <LanguageButton onMenuItemClick={this.handleMenuItemClick} />}
                <CommentsButton onMenuItemClick={this.handleMenuItemClick} />
              </div>
              <div className="kuika-left-menu__menu-items">
                <AuditButton onMenuItemClick={this.handleMenuItemClick} />
                <KuikaButton
                  data-testid="leftMenuBoardButton"
                  data-onboarding-id="left_menu_board"
                  className="kuika-left-menu__board-button"
                  type="tertiary"
                  size="md"
                  isIcon
                  color="neutral"
                  active={this.state.isBoardOverlayVisible}
                  onClick={this.toggleBoardOverlay}
                  style={{ position: "relative" }}
                >
                  <KuikaSvg type="sidebar-icons" name="board" width={18} height={18} pointer style={{ minWidth: 18 }} />
                  <BoardNavBadge />
                </KuikaButton>
                <HelpdeskButton />
                {/* <ChatbotButton
                  isChatbotPanelVisible={this.state.isChatbotPanelVisible}
                  setIsChatbotPanelVisible={this.setIsChatbotPanelVisible}
                  onMenuItemClick={this.handleMenuItemClick}
                /> */}
                {KMainFunctions.isMainAI() ? (
                  <CustomChatbot
                    isChatbotPanelVisible={this.state.isChatbotPanelVisible}
                    setIsChatbotPanelVisible={this.setIsChatbotPanelVisible}
                  />
                ) : (
                  <ChatbotWindow
                    isChatbotPanelVisible={this.state.isChatbotPanelVisible}
                    setIsChatbotPanelVisible={this.setIsChatbotPanelVisible}
                  />
                )}
                {/* <ChatbotWindow
                  isChatbotPanelVisible={this.state.isChatbotPanelVisible}
                  setIsChatbotPanelVisible={this.setIsChatbotPanelVisible}
                />
                <CustomChatbot
                  isChatbotPanelVisible={this.state.isChatbotPanelVisible}
                  setIsChatbotPanelVisible={this.setIsChatbotPanelVisible}
                /> */}
                {!isMainAI && <AcademyButton onMenuItemClick={this.handleMenuItemClick} />}
                <ShortCutInfo />
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  checkNeedRender = (
    changeReason: ESdChangeReason,
    nextProps: IDesignLeftMenuProps & IWithSDProps & IWithContextProp
  ): boolean => {
    // Floating-chat visibility moved onto the SD context, so it no longer arrives as a local
    // setState that re-renders on its own — this is the one sd field this menu must react to.
    return nextProps.sd.isMcpChatVisible !== this.props.sd.isMcpChatVisible;
  };
}

const designLeftMenu = withContext<IDesignLeftMenuProps>(withSD<IDesignLeftMenuProps>(DesignLeftMenu));
export { designLeftMenu as DesignLeftMenu };
